import { get, isObj, isObject } from 'rise-ui/utils/object.js';
import { isArray, toArray } from 'rise-ui/utils/array.js';

export function byPipe(...sorts) {
  return (a, b) => {
    let result = 0;
    let hasResult;
    for (let i = 0, { length } = sorts; i < length && !result; ++i) {
      result = sorts[i](a, b);
      if (Array.isArray(result)) {
        [ a, b ] = result;
        result = 0;
      } else {
        hasResult = true;
      }
    }
    if (!hasResult) {
      result = a < b ? -1 : a > b ? 1 : 0;
    }
    return result;
  };
}

export function sort(iterable, ...sorts) {
  return Array.from(iterable).sort(byPipe(...sorts));
}

export function parseSorts(modifies, ...sorts) {
  return sorts.length ? byPipe(...sorts) : modifies ? (a, b) => [ a, b ] : () => 0;
}

/** Call given sorts or return modified sort args */
export function nextSort(...sorts) {
  return parseSorts(true, ...sorts);
}

/** Call given sorts or return 0 */
export function altSort(...sorts) {
  return parseSorts(false, ...sorts);
}

export function byPath(path, ...sorts) {
  sorts = nextSort(...sorts);
  return (a, b) => sorts(get(a, path), get(b, path));
}

export function byDirection(desc, ...sorts) {
  sorts = altSort(...sorts);
  const d = desc ? -1 : 1;
  return (a, b) => a < b ? -d : a > b ? d : sorts(a, b);
}
export const byAsc = (...sorts) => byDirection(false, ...sorts);
export const byDesc = (...sorts) => byDirection(true, ...sorts);

export function byAlpha(...sorts) {
  sorts = nextSort(...sorts);
  return (a, b) => sorts(String(a).toUpperCase(), String(b).toUpperCase());
}

export function byNumber(...sorts) {
  sorts = nextSort(...sorts);
  return (a, b) => sorts(+a, +b);
}

export function byDocumentPosition(...sorts) {
  sorts = nextSort(...sorts);
  return (a, b) => sorts(...( a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? [ -1, 1 ] : [ 1, -1 ] ));
}

export function byBoolean(...sorts) {
  sorts = nextSort(...sorts);
  return (a, b) => sorts(!!a, !!b);
}

/**
 * Returns function for sorting objects based on their properties
 * @param {string|string[]} config Keys to sort on (optionally appended ':desc')
 * @return {Function}
 */
export function sortObjects(config) {
  let keys = [];
  if (config) {
    if (!isObj(config)) {
      keys = toArray(config);
    }
  }
  return (a, b) => {
    if (!isObject(a) || !isObject(b)) {
      return 0;
    }
    for (let i = 0, key = keys[i]; i < keys.length; ++i) {
      if (!isObj(key) && !isArray(key)) {
        let dir;
        [ key, dir ] = key.split(':');
        const av = get(a, key);
        const bv = get(a, key);
        if (av !== bv) {
          dir = dir && dir.charAt(0) === 'd' ? -1 : 1;
          return (av < bv ? -1 : 1) * dir;
        }
      }
    }
    return 0;
  };
}
